const { Component, DOMUtils, Utils } = window.Liftoff;

const MAXIMUM_STAR_RATING = 5;
const FILLED_STAR_CHAR = "&#9733;";
const EMPTY_STAR_CHAR = "&#9734;";

const TEMPLATE = `
  <div class="product-tile-banner loading">
    <div class="icon switch-with-component"></div>
    <div class="text-container switch-with-component">
      <div class="app-name"></div>
      <div class="star-rating">
        <div class="empty-stars">${new Array(MAXIMUM_STAR_RATING)
          .fill(EMPTY_STAR_CHAR)
          .join("")}</div>
        <div class="filled-stars">${new Array(MAXIMUM_STAR_RATING)
          .fill(FILLED_STAR_CHAR)
          .join("")}</div>
      </div>
      <div class="caption"></div>
    </div>
    <div class="outer-cta-container switch-with-component">
      <div class="cta-container"></div>
    </div>
    <div class="component-container exited"></div>
  </div>
`;

Component.register(
  "product-tile-banner",
  class ProductTileBanner extends Component {
    constructor(config) {
      super(config);
      Object.assign(
        this,
        Utils.mergePropertiesWithDefaults(
          {
            icon: null,
            iconStyles: {},
            appName: "",
            appNameStyles: {},
            starRating: null,
            starStyles: {},
            caption: null,
            captionStyles: {},
            cta: null,
            ctaStyles: {},
            component: null,
            componentStyles: {},
            switchElements: "all",
            appInfoDuration: 1800,
            componentDuration: 2000,
            switchingDuration: 500,
            initialDelay: 1800,
            appNameLineLimit: null,
            forceFullscreenWidth: false,
          },
          config
        )
      );
      this.transitionEndHandler = (e) => {
        // We only want to listen to transition events coming from elements in this component (not any bubbling
        // events from child components). Within this component, "transform" and "visibility" properties are
        // both transitioned and both will independently produce transitionend events. But we only want to
        // respond to one transition per element regardless of the number of properties transitioned.
        if (e.propertyName !== "transform" || e.target !== e.currentTarget)
          return;
        this.animateNext();
      };
      this.ready = false;
      this.startWhenReady = false;
      this.firstAnimationComplete = false;
      this.running = false;
      if (this.starRating != null) {
        this.starRating = Math.min(
          Math.max(0, this.starRating),
          MAXIMUM_STAR_RATING
        );
      }
    }

    load() {
      const loadPromises = [this.component, this.caption, this.cta]
        .filter((c) => c)
        .map((c) => c.load());
      if (this.icon != null) {
        const imageLoadPromise = Utils.loadImage(this.icon).then(
          (imgEl) => (this.iconImgEl = imgEl)
        );
        loadPromises.push(imageLoadPromise);
      }

      this.loaded = Promise.all(loadPromises);
      return this.loaded;
    }

    layout(parentNode) {
      super.layout(parentNode);

      this.el = DOMUtils.strToElement(TEMPLATE);
      if (parentNode.getBoundingClientRect().height >= 80) {
        this.el.classList.add("tablet");
      }

      const captionEl = this.el.querySelector(".caption");
      Object.assign(captionEl.style, this.captionStyles);
      [...this.el.children].forEach((childEl) => {
        childEl.style.transitionDuration = `${this.switchingDuration}ms`;
      });

      this.parentNode.appendChild(this.el);
      this.loaded
        .then(() => {
          if (this.cta != null) {
            const ctaContainer = this.el.querySelector(".cta-container");
            Object.assign(ctaContainer.style, this.ctaStyles);
            this.cta.layout(ctaContainer);
          } else {
            this.el.querySelector(".outer-cta-container").remove();
          }

          if (this.starRating != null) {
            this.setStarRating();
            Object.assign(
              this.el.querySelector(".star-rating").style,
              this.starStyles
            );
          } else {
            this.el.querySelector(".star-rating").remove();
          }

          if (this.icon == null) {
            return Promise.resolve();
          }

          this.el.querySelector(".icon").appendChild(this.iconImgEl);
          Object.assign(this.iconImgEl.style, this.iconStyles);
          return DOMUtils.imageIsRendered(this.iconImgEl);
        })
        .then(() => {
          const appNameEl = this.el.querySelector(".app-name");
          Object.assign(appNameEl.style, this.appNameStyles);
          if (this.appNameLineLimit != null) {
            appNameEl.style.visiblity = "hidden";
            // We fill the element with a number of line breaks equal to the line limit to determine the height of
            // the element were the maximum number of lines to be used.
            const limitString = Array(this.appNameLineLimit)
              .fill("<br>")
              .join("");
            const conformsToLineLimit = () => {
              appNameEl.innerHTML = limitString;
              const limitHeight = appNameEl.getBoundingClientRect().height;
              appNameEl.innerHTML = this.appName;
              const populatedHeight = appNameEl.getBoundingClientRect().height;
              // If the user specified a height via CSS, then populatedHeight will always === limitHeight.
              // In this case, we assume the user's intent is to make the text fit into the given height,
              // so we also check `scrollHeight`.
              return (
                populatedHeight <= limitHeight &&
                appNameEl.scrollHeight <= populatedHeight
              );
            };
            let fontSize = parseInt(
              window.getComputedStyle(appNameEl).fontSize
            );
            while (!conformsToLineLimit() && fontSize > 0) {
              fontSize -= 1;
              Object.assign(appNameEl.style, {
                fontSize: `${fontSize}px`,
                lineHeight: `${fontSize * 1.1}px`,
              });
            }
            appNameEl.style.visbility = "visible";
          }
          appNameEl.innerHTML = this.appName;

          if (this.caption != null) {
            const captionContainerEl = this.el.querySelector(".caption");
            // NOTE(Casey): On Safari, without position: absolute on the caption
            // container during subcomponent layout, children with width: 100%,
            // height: 100% will be sized relative to the nearest positioned
            // ancestor, possibly resulting in a caption that extends beyond the
            // bounds of the component container. We reset the position after layout
            // to attain proper positioning relative to sibling elements.
            captionContainerEl.style.position = "absolute";
            this.caption.layout(this.el.querySelector(".caption"));
            captionContainerEl.style.height = `${
              captionContainerEl.getBoundingClientRect().height
            }px`;
            captionContainerEl.style.position = "relative";
          } else {
            this.el.querySelector(".caption").remove();
          }
        })
        .then(() => {
          if (this.component != null) {
            this.componentContainerEl = this.el.querySelector(
              ".component-container"
            );
            Object.assign(
              this.componentContainerEl.style,
              this.componentStyles
            );
            this.setComponentContainerSize();

            // Force component to take full screen width
            if (this.forceFullscreenWidth) {
              this.componentContainerEl.style.width = "100%";
              this.componentContainerEl.style.left = 0;

              // Increase z-index value for icon and cta containers so that they
              // appear as overlay on the component area
              this.el.querySelector(".outer-cta-container").style.zIndex = 2;
              this.el.querySelector(".icon").style.zIndex = 2;
            }

            this.component.layout(this.componentContainerEl);
            DOMUtils.addOneTimeListener(this.el, "transitionend", () => {
              this.firstAnimationComplete = true;
            });
          }

          this.ready = true;
          this.el.classList.remove("loading");
          if (this.startWhenReady) {
            this.start();
          }
        });
    }

    setComponentContainerSize() {
      let left = 0;
      let right = 0;
      if (this.switchElements === "all but cta" && this.cta != null) {
        const ctaContainerEl = this.el.querySelector(".outer-cta-container");
        right =
          this.el.getBoundingClientRect().right -
          ctaContainerEl.getBoundingClientRect().left;
        ctaContainerEl.classList.remove("switch-with-component");
      } else if (this.switchElements === "all but icon" && this.icon != null) {
        DOMUtils.forceReflow(this.el.querySelector(".icon"));
        left = this.el.querySelector(".icon").getBoundingClientRect().width;
        this.el
          .querySelector(".icon")
          .classList.remove("switch-with-component");
      }

      Object.assign(this.componentContainerEl.style, {
        right: `${right}px`,
        left: `${left}px`,
      });
    }

    setStarRating() {
      const filledStarEl = this.el.querySelector(".filled-stars");
      let filledStarWidth =
        (this.starRating / MAXIMUM_STAR_RATING) *
        filledStarEl.getBoundingClientRect().width;
      // Flooring and subtracting 0.5 was determined empirically to produce a visualization that worked well
      // for all whole and half values.
      filledStarWidth = Math.max(Math.floor(filledStarWidth - 0.5), 0);
      filledStarEl.style.width = `${filledStarWidth}px`;
    }

    animateNext() {
      const switchingEls = [
        ...this.el.querySelectorAll(".switch-with-component"),
      ];
      const enterEl = (el) => {
        el.classList.add("staged");
        el.classList.remove("exited");
        DOMUtils.forceReflow(el);
        el.classList.remove("staged");
      };
      const exitEl = (el) => el.classList.add("exited");

      if (this.componentContainerEl.classList.contains("exited")) {
        // Animate component in.
        const animationDelay = this.firstAnimationComplete
          ? this.appInfoDuration
          : this.initialDelay;
        this.component.stop();
        this.animationTimeout = setTimeout(() => {
          switchingEls.forEach(exitEl);
          enterEl(this.componentContainerEl);
          this.component.start();
        }, animationDelay);
      } else {
        // Animate app info in.
        this.animationTimeout = setTimeout(() => {
          switchingEls.forEach(enterEl);
          exitEl(this.componentContainerEl);
        }, this.componentDuration);
      }
    }

    resize() {
      if (this.component == null) return;
      this.setComponentContainerSize();
      this.component.resize();
      this.setStarRating();
    }

    restart() {
      this.stop();
      this.firstAnimationComplete = false;
      this.start();
    }

    start() {
      this.startWhenReady = true;
      if (!this.ready || this.running) return;
      if (this.cta != null) {
        this.cta.start();
      }
      if (this.caption != null) {
        this.caption.start();
      }

      if (this.component == null) return;
      this.running = true;
      this.componentContainerEl.addEventListener(
        "transitionend",
        this.transitionEndHandler
      );
      this.animateNext();
    }

    stop() {
      this.startWhenReady = false;
      if (!this.running) return;
      if (this.cta != null) {
        this.cta.stop();
      }
      if (this.caption != null) {
        this.caption.stop();
      }

      if (this.component == null) return;
      clearTimeout(this.animationTimeout);
      this.componentContainerEl.removeEventListener(
        "transitionend",
        this.transitionEndHandler
      );
      this.running = false;
      [...this.el.children].forEach((childEl) => {
        // Stop the current transition by setting transition: none, and then reset the style.
        childEl.style.transition = "none";
        DOMUtils.forceReflow(childEl);
        // When the component stops, make sure the app info (rather than the component) is displayed.
        childEl.classList.remove("staged");
        if (childEl === this.componentContainerEl) {
          childEl.classList.add("exited");
        } else {
          childEl.classList.remove("exited");
        }

        DOMUtils.forceReflow(childEl);
        childEl.style.transition = null;
      });
      this.component.stop();
    }
  }
);



// WEBPACK FOOTER //
// ./components/product-tile-banner/product-tile-banner.js